import { NodeType } from '@/enums/node-type.enum';
import { toolPreviewValue } from './toolPreviewValue';
import type { DesignAppInput } from '@/types/design-app.types';
import type { ToolConfig, ToolConfigInput, ToolConfigInputDefault } from '@/types/tool-config.types';

type PreviewFile = {
  url?: string;
  blobstore?: { id?: string };
};

/** A row written by the old Design App save, which stored builder settings next to the fill. */
export type SavedDesignAppParam = {
  value?: unknown;
  order?: number;
  exposed?: boolean;
  description?: string | null;
  disabled?: boolean;
  required?: boolean;
};

type ParamsRecord = Pick<DesignAppInput, 'id' | 'type' | 'value'>;

/**
 * `outputs` being an array, including an empty one, means this version has a toolConfig.
 * A missing or unreadable column does not.
 */
export function toolConfigDeclaresTool(toolConfig: ToolConfig | null | undefined): boolean {
  return Array.isArray(toolConfig?.outputs);
}

function previewFile(value: unknown): PreviewFile | null | undefined {
  if (!value || typeof value !== 'object' || !('file' in value)) {
    return undefined;
  }
  return (value as { file?: PreviewFile | null }).file;
}

function fileLocator(value: unknown): string {
  const file = previewFile(value);
  if (!file || typeof file !== 'object') {
    return '';
  }
  return file.blobstore?.id ?? file.url ?? '';
}

function promptText(value: unknown): string | undefined {
  if (!value || typeof value !== 'object' || !('prompt' in value)) {
    return undefined;
  }
  const prompt = (value as { prompt?: unknown }).prompt;
  return typeof prompt === 'string' ? prompt : undefined;
}

function stringText(value: unknown): string | undefined {
  if (!value || typeof value !== 'object' || !('string' in value) || typeof value.string !== 'string') {
    return undefined;
  }
  return value.string;
}

/**
 * True when the viewer's fill is still the toolConfig default.
 * Signed URLs differ across loads, so files compare on blobstore id, then durable URL.
 */
export function previewValueMatchesToolDefault(
  nodeType: string,
  value: unknown,
  storedDefault: ToolConfigInputDefault | undefined,
): boolean {
  const baseline = toolPreviewValue(nodeType, storedDefault);
  if (baseline === undefined) {
    return false;
  }
  if (nodeType === NodeType.Prompt || nodeType === NodeType.PromptV3) {
    return promptText(value) === promptText(baseline);
  }
  if (nodeType === NodeType.String) {
    return stringText(value) === stringText(baseline);
  }
  if (nodeType === NodeType.Import) {
    return fileLocator(value) === fileLocator(baseline);
  }
  return false;
}

/**
 * The personal fill to restore. A value that still matches the current toolConfig default is not a
 * fill. Extra builder fields do not make a row a copied default: the tool view still saves them.
 */
export function restorableParamsValue(
  nodeType: string,
  saved: SavedDesignAppParam | undefined,
  storedDefault: ToolConfigInputDefault | undefined,
): DesignAppInput['value'] | undefined {
  if (!saved || saved.value === undefined) {
    return undefined;
  }
  if (previewValueMatchesToolDefault(nodeType, saved.value, storedDefault)) {
    return undefined;
  }
  return saved.value as DesignAppInput['value'];
}

/**
 * A preview value in the shape of a toolConfig default, so a "Use node value" input can be compared
 * with what its node holds the same way a stored default is.
 */
export function viewerDefault(value: unknown): ToolConfigInputDefault | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  if ('prompt' in value || 'string' in value || 'file' in value) {
    return value as ToolConfigInputDefault;
  }
  return undefined;
}

/** The tool view's run-data descriptors, in the shape `designAppParamsPayload` already compares. */
export function designAppParamsFromViewer(
  inputs: DesignAppInput[],
  descriptors: { nodeId: string; exposed: boolean; defaultValue?: unknown }[],
): DesignAppInput[] | ParamsRecord[] {
  const configInputs: ToolConfigInput[] = descriptors.map((descriptor) => {
    const defaultValue = viewerDefault(descriptor.defaultValue);
    return {
      nodeId: descriptor.nodeId,
      exposed: descriptor.exposed,
      ...(defaultValue ? { defaultValue } : {}),
    };
  });
  return designAppParamsPayload(inputs, { version: 1, outputs: [], inputs: configInputs });
}

/**
 * What to write to `design_app_params` while the Tool tab (`workflow-app-builder-mode`) is on. With a
 * toolConfig, builder fields stay off the row, and a value that still equals its default is left out
 * so the next open can follow a new default. Without a toolConfig the legacy full input is saved unchanged.
 */
export function designAppParamsPayload(
  inputs: DesignAppInput[],
  toolConfig: ToolConfig | null | undefined,
  /** For inputs with no stored default ("Use node value"): what their node holds. */
  nodeDefaults?: ReadonlyMap<string, ToolConfigInputDefault>,
): DesignAppInput[] | ParamsRecord[] {
  if (!toolConfigDeclaresTool(toolConfig)) {
    return inputs;
  }
  const defaults = new Map((toolConfig?.inputs ?? []).map((input) => [input.nodeId, input.defaultValue]));
  return inputs.flatMap((input) => {
    const baseline = defaults.get(input.id) ?? nodeDefaults?.get(input.id);
    if (previewValueMatchesToolDefault(input.type, input.value, baseline)) {
      return [];
    }
    return [{ id: input.id, type: input.type, value: input.value }];
  });
}
